import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categoryLinks, mainSiteUrl, navLinks } from '@/mocks/homeData';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileCatOpen(false);
    setDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-background-50/85 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-950 hover:text-primary-600 transition-colors duration-200 flex-shrink-0"
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-file-excel-2-line text-2xl text-primary-500"></i>
            </div>
            <span className="font-heading text-lg whitespace-nowrap">BaseTao Sheet</span>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.slice(0, 2).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                  location.pathname === link.path
                    ? 'text-primary-600 bg-primary-100/70'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                {link.name}
              </Link>
            ))}

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="ml-1 px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Shop Categories
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${dropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm transition-transform`}></i>
                </div>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-background-50 border border-background-200/70 rounded-xl shadow-lg overflow-hidden animate-fade-in">
                  <div className="max-h-[420px] overflow-y-auto py-2">
                    {categoryLinks.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 whitespace-nowrap"
                      >
                        <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                          <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                        </div>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="w-px h-6 bg-background-200/70 mx-2"></div>

            {navLinks.slice(2).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-2.5 py-2 rounded-md text-xs font-medium transition-colors duration-200 whitespace-nowrap ${
                  location.pathname === link.path
                    ? 'text-primary-600 bg-primary-100/70'
                    : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                {link.name}
              </Link>
            ))}

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-5 py-2 bg-accent-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-background-200/60 py-4 animate-fade-in max-h-[calc(100vh-64px)] overflow-y-auto">
            <div className="space-y-1 mb-3">
              {navLinks.slice(0, 2).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    location.pathname === link.path
                      ? 'text-primary-600 bg-primary-100/70'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`${link.path === '/' ? 'ri-home-4-line' : 'ri-guide-line'} text-lg`}></i>
                  </div>
                  {link.name}
                </Link>
              ))}
            </div>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between px-3 py-3 rounded-lg text-sm font-semibold bg-primary-500 text-background-50 transition-colors duration-200 cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-grid-line text-lg"></i>
                </div>
                Shop Categories
              </div>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${mobileCatOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} transition-transform`}></i>
              </div>
            </button>

            {mobileCatOpen && (
              <div className="mt-2 grid grid-cols-2 gap-2 animate-fade-in">
                {categoryLinks.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-background-100 border border-background-200/60 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200"
                  >
                    <div className="w-full aspect-square rounded-lg overflow-hidden">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-xs font-medium text-foreground-700 text-center leading-tight">
                      {cat.name}
                    </span>
                  </a>
                ))}
              </div>
            )}

            <div className="mt-3 space-y-1">
              <div className="px-3 py-1 text-xs text-foreground-400 font-medium uppercase tracking-wider">
                SEO Resources
              </div>
              {navLinks.slice(2).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-3 py-2.5 rounded-lg text-sm transition-colors duration-200 ${
                    location.pathname === link.path
                      ? 'text-primary-600 bg-primary-100/70'
                      : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 block w-full text-center px-5 py-3 bg-accent-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-accent-600 transition-colors duration-200"
            >
              Buy It Now on BaseTao Spreadsheet
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}